import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, } from '@angular/core';
import { URLS } from '../../data/navigation/navigation.data';
import { Product, ProductLine } from '../../model/product/product.model';
import { ProductService } from '../../service/product/product.service';
import { UtilService } from '../../service/util/util.service';
import { placeCaret } from '../../util/popover-caret';
import { DropdownCoordinatorService } from './dropdown-coordinator.service';

/** A simple, non-product menu entry (e.g. an appearance option). */
export interface DropdownMenuItem {
  value: string;
  label: string;
  description?: string;
  variant?: string; // moon swatch variant: 'light' | 'dark' | 'both'
  icon?: string;    // or a FontAwesome class
  swatch?: string;  // or a solid color dot (e.g. a theme's primary color)
  logo?: string;    // or a logo image URL (e.g. a theme's icon)
  colors?: string[]; // a row of colour circles under the label (a theme's five identity colours, owner 2026-10-13)
  selected?: boolean;
  divider?: boolean; // draws a separator above this item (groups it apart)
  header?: boolean;  // renders as a non-clickable sub-header (a sub-layer label)
  minorHeader?: boolean;  // with header: a second-level label under a sub-header (e.g. Light / Dark under Nature)
  disabled?: boolean; // dimmed + non-clickable (e.g. a mode a theme can't use)
}

/** A titled group of generic items (e.g. "Appearance"). Lets a Settings
 *  menu hold several sub-sections, each of which may carry sub-layer headers
 *  (items with `header: true`). */
export interface DropdownMenuSection {
  title?: string;
  description?: string;
  items: DropdownMenuItem[];
  /** rows under `items`, each a sub-header over item lists side by side, in one wide box (Default's three modes,
   *  then Nature | Cities, in Themes, owner 2026-10-16) */
  rows?: DropdownMenuRow[];
}

export interface DropdownMenuRow {
  header?: string;
  columns: DropdownMenuItem[][];
}

/**
 * One dropdown menu, fully self-contained. Every consumer (Services, Plugins,
 * Tools, Settings, in both the top nav and the bottom nav) wires its trigger
 * the exact same way:
 *
 *   <a (mouseenter)="m.hoverOpen()"
 *      (mouseleave)="m.scheduleClose()"
 *      (click)="m.toggle( $event )">
 *     <app-dropdown-menu #m [productLines]="..."> trigger label </app-dropdown-menu>
 *   </a>
 *
 * Interaction rules (identical for all menus):
 *  - Desktop: hovering the trigger opens it; leaving the trigger or the panel
 *    starts a short grace timer so crossing the gap between them doesn't snap
 *    it shut; re-entering either cancels the timer. Clicking the trigger
 *    toggles.
 *  - Mobile (<= 999px): no hover, tapping the trigger toggles a full-screen
 *    panel. Hover handlers no-op on small screens.
 *  - Opening one menu closes all others (via DropdownCoordinatorService).
 *  - Clicking outside the menu closes it; selecting an item closes it unless
 *    the menu opts out via `closeOnSelect` (the Appearance menu stays open so
 *    several modes/themes can be tried in a row).
 */
@Component( {
  selector: 'app-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styleUrls: [ './dropdown-menu.component.scss' ],
} )
export class DropdownMenuComponent implements OnInit, OnDestroy {
  URLS = URLS;

  @Input() isDropdownOpen = false;
  @Input() minimumColumns = 1;
  @Input() minimumRows = 6;
  // Anchor the panel to its trigger's right edge (extends leftward) instead of
  // the default left anchor, for triggers near the right of the nav, so a
  // wide multi-column panel doesn't overflow off-screen.
  @Input() alignRight = false;
  // the macOS menu bar popover look of the nav's Contact Us (contact-popover.component.scss, owner 2026-10-12)
  @Input() macPopover = false;

  calculatedColumns;
  calculatedRows;

  @Input() productLines: ProductLine[] = [];

  // Generic (non-product) mode: when `sections` is set the menu renders these
  // titled groups of items instead of product columns, and emits `itemSelect`.
  @Input() sections: DropdownMenuSection[] = null;
  @Output() itemSelect = new EventEmitter<string>();

  // Whether picking a generic item closes the menu. The Appearance menu sets
  // this false so the user can try several modes/themes in a row. Mobile
  // ignores it, the full-screen panel must still dismiss on tap.
  @Input() closeOnSelect = true;

  // trackBy keeps Angular reusing the section/item DOM nodes even when the
  // `sections` getter hands back a fresh array each change-detection cycle,   // without it the rows are torn down and rebuilt on every CD, which drops the
  // :hover state mid-pointer ("blinking" / dead hover on desktop).
  trackSection = ( _: number, section: DropdownMenuSection ) => section.title;
  trackItem = ( _: number, item: DropdownMenuItem ) => ( item.header ? 'h:' : '' ) + item.value;

  closeDropdownInternalClicked = false;

  // Grace period (ms) before a hover-out actually closes the menu, so a quick
  // move across the gap between the trigger and the panel doesn't snap it shut.
  private static readonly CLOSE_GRACE_MS = 220;
  private closeTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private host: ElementRef,
    private utilService: UtilService,
    private productService: ProductService,
    private coordinator: DropdownCoordinatorService ) {
    this.calculatedColumns = this.minimumColumns;
    this.calculatedRows = this.minimumRows;
  }

  ngOnInit() {
    this.coordinator.register( this );
  }

  ngOnDestroy() {
    this.cancelCloseTimer();
    this.coordinator.unregister( this );
  }

  // ---- Public trigger API (identical wiring for every menu) -----------------

  /** Trigger hovered (hover-capable pointers only): open this menu, close the
   *  rest. Skipped on touch, there a tap synthesizes mouseenter+click, so
   *  hover-opening would let the click immediately toggle the menu back shut. */
  hoverOpen() {
    if ( !this.utilService.supportsHover() ) {
      return;
    }
    this.open();
  }

  /** Trigger tapped/clicked: toggle. Works on both desktop and mobile. */
  toggle( event?: Event ) {
    event?.stopPropagation();
    if ( this.isDropdownOpen ) {
      this.closeDropdown();
    } else {
      this.open();
    }
  }

  /** Pointer left the trigger or the panel: close after a short grace period.
   *  Only meaningful for hover-capable pointers, touch has no hover-out. */
  scheduleClose() {
    if ( !this.utilService.supportsHover() || !this.isDropdownOpen || this.closeTimer !== null ) {
      return;
    }
    this.closeTimer = setTimeout( () => {
      this.closeTimer = null;
      this.closeDropdown();
    }, DropdownMenuComponent.CLOSE_GRACE_MS );
  }

  /** Pointer is over the trigger or panel again: keep the menu open. */
  keepOpen() {
    this.cancelCloseTimer();
  }

  // ---- Open / close ---------------------------------------------------------

  private open() {
    this.cancelCloseTimer();
    this.coordinator.closeOthers( this );
    this.isDropdownOpen = true;
    this.closeDropdownInternalClicked = true;
    this.onResize();
    if ( this.macPopover ) {
      // the pin under the middle of the trigger's label
      setTimeout( () => {
        const element: HTMLElement = this.host.nativeElement;
        placeCaret( element.querySelector( '.dropdown-content' ), element.querySelector( '.ng-content-area' ) );
      }, 0 );
    }
  }

  closeDropdown() {
    this.cancelCloseTimer();
    this.isDropdownOpen = false;
    this.closeDropdownInternalClicked = false;
  }

  private cancelCloseTimer() {
    if ( this.closeTimer !== null ) {
      clearTimeout( this.closeTimer );
      this.closeTimer = null;
    }
  }

  /** Any click outside this menu closes it. Clicks on the trigger and on items
   *  are handled by their own handlers, so they are ignored here. */
  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event ) {
    if ( this.isDropdownOpen && !this.host.nativeElement.contains( event.target ) ) {
      this.closeDropdown();
    }
  }

  @HostListener( 'window:resize' )
  onResize() {
    if ( this.isDropdownOpen && window.innerWidth > 999 ) {
      let wantedColumns = this.minimumColumns;

      while ( wantedColumns * 330 > window.innerWidth - 240 ) {
        wantedColumns = wantedColumns - 2;
      }

      this.calculatedColumns = wantedColumns;

      if ( this.calculatedColumns < this.minimumColumns ) {
        this.calculatedRows =
          this.minimumRows - ( this.minimumColumns - this.calculatedColumns + 2 );
      } else {
        this.calculatedRows = this.minimumRows;
      }
    }
  }

  // ---- Item selection -------------------------------------------------------

  setImageLoaded = ( product: Product ) => {
    product.imageLoaded = true;
  };

  /** Generic-mode item click: emit the value and close (unless the menu opts
   *  out via `closeOnSelect`). stopPropagation keeps the click from bubbling
   *  to the trigger's toggle, which would re-open it. */
  selectItem( item: DropdownMenuItem, event?: Event ) {
    event?.stopPropagation();
    if ( item.disabled ) {
      return;
    }
    this.closeDropdownInternalClicked = true;
    this.itemSelect.emit( item.value );
    if ( this.closeOnSelect || this.utilService.isSmallScreen() ) {
      this.closeDropdown();
    }
  }

  /** Crawlable destination for a product item, see ProductService.productUrl. */
  productUrl( product: Product, productLine?: ProductLine ): string {
    return this.productService.productUrl( product, productLine );
  }

  closeDropDownInternal( product?: Product, productLine?: ProductLine, event?: Event ) {
    // The anchor carries a real href for crawlers; navigation is handled in JS
    // (fragment scroll / external open), so suppress the default page load.
    event?.preventDefault();
    this.closeDropdownInternalClicked = true;
    if ( product && productLine ) {
      this.redirectToProductUrl( product, productLine );
    }
    this.utilService.delay( 500 ).then( () => {
      this.closeDropdown();
    } );
  }

  getProductLineMaxHeight() {
    if ( this.utilService.isSmallScreen() || this.calculatedRows >= this.minimumRows ) {
      return '';
    }

    return 'max-height: ' + ( 60 + 150 * this.calculatedRows + 1 ) + 'px;';
  }

  redirectToProductUrl( product: Product, productLine: ProductLine ) {
    this.productService.redirectToProductUrl( product, productLine );
  }
}
